import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { MapPin, Users, Activity, ArrowRight, ArrowLeft, ListFilter } from "lucide-react";
import { Button } from "../components/ui/button";
import HealthScore from "../components/HealthScore";
import BPMChart from "../components/BPMChart";
import KPIDashboard from "../components/KPIDashboard";

const SIGNAL_LABELS_FR = {
  recos_de_faible_qualite: "Qualité des recommandations à renforcer",
  pas_assez_invites: "Flux d'invités insuffisant",
  "121_en_baisse": "Dynamique des 1-2-1 en recul",
  presence_en_baisse: "Assiduité en baisse",
  business_concentre: "Business concentré sur peu de membres",
};
const SIGNAL_LABELS_EN = {
  recos_de_faible_qualite: "Recommendation quality to strengthen",
  pas_assez_invites: "Insufficient guest flow",
  "121_en_baisse": "Declining 1-2-1 momentum",
  presence_en_baisse: "Declining attendance",
  business_concentre: "Business concentrated on few members",
};

export default function RegionalDashboardPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const [selected, setSelected] = useState([]);
  const [symptomRef, setSymptomRef] = useState({});
  const uiLocale = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const signalDict = uiLocale === "en" ? SIGNAL_LABELS_EN : SIGNAL_LABELS_FR;
  const labelFor = (code) => {
    const ref = symptomRef[code];
    if (ref) return uiLocale === "en" ? ref.label_en : ref.label_fr;
    return signalDict[code] || String(code).replace(/_/g, " ");
  };

  useEffect(() => {
    api.get("/dashboard/regional")
      .then((r) => setData(r.data))
      .catch((e) => setErrorStatus(e?.response?.status || 0))
      .finally(() => setLoading(false));
    api.get("/reference/symptoms")
      .then((r) => {
        const map = {};
        (r.data || []).forEach((s) => { map[s.code] = s; });
        setSymptomRef(map);
      })
      .catch(() => {});
  }, []);

  if (loading) return <div className="h-96 bg-bg-soft rounded-xl animate-pulse" data-testid="regional-loading" />;
  if (errorStatus === 403) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="regional-access-denied">
        <div className="text-4xl mb-4">🔒</div>
        <h2 className="font-heading font-bold text-xl mb-2">{t("regional.access_denied_title")}</h2>
        <p className="text-text-secondary">{t("regional.access_denied_body")}</p>
      </div>
    );
  }
  if (errorStatus) {
    return <div className="text-center text-text-secondary py-16" data-testid="regional-load-error">{t("regional.load_error")}</div>;
  }
  if (!data) return <div className="text-center text-text-secondary" data-testid="regional-no-data">{uiLocale === "en" ? "No regional data" : "Aucune donnée régionale"}</div>;

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 3 ? [...prev, id] : prev);
  };

  const comparison = data.groups.filter((g) => selected.includes(g.id));

  return (
    <div className="space-y-6" data-testid="regional-dashboard">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-apollo-orange-dark" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> {t("nav.back")}
      </Link>

      <header>
        <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">{t("regional.header_title")}</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">{data.scope_region}</h1>
        <p className="font-oracle italic text-lg text-text-secondary mt-2">{uiLocale === "en" ? `"${data.total_groups} group(s) under your watch."` : `« ${data.total_groups} groupe(s) sous votre regard. »`}</p>
      </header>

      {data.common_symptoms?.length > 0 && (
        <section className="bg-gradient-to-br from-apollo-orange-light/40 via-white to-bg-soft border border-border rounded-2xl p-6" data-testid="patterns-section">
          <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-3"><Activity className="w-5 h-5 text-apollo-orange" />{uiLocale === "en" ? "Detected patterns" : "Patterns détectés"}</h2>
          <p className="text-sm text-text-secondary mb-3">{t("regional.top_signals")} :</p>
          <div className="space-y-1.5">
            {data.common_symptoms.map((s) => (
              <div key={s.code} className="flex items-center gap-2" data-testid={`pattern-${s.code}`}>
                <div className="flex-1 bg-bg-soft rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-apollo-orange" style={{ width: `${Math.min(100, (s.count / data.total_groups) * 100)}%` }} />
                </div>
                <span className="text-xs font-mono text-text-secondary w-8 text-right">{s.count} ×</span>
                <span className="text-sm font-medium w-72">{labelFor(s.code)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-white border border-border rounded-2xl p-6" data-testid="groups-table-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl flex items-center gap-2"><MapPin className="w-5 h-5 text-apollo-orange" />{uiLocale === "en" ? "Your groups" : "Vos groupes"}</h2>
          {selected.length > 0 && (
            <span className="text-xs text-text-secondary"><ListFilter className="w-3 h-3 inline mr-1" />{selected.length}/3 {uiLocale === "en" ? "selected for comparison" : "sélectionnés pour comparaison"}</span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.groups.map((g) => {
            const isSelected = selected.includes(g.id);
            return (
              <div key={g.id} className={`border rounded-xl p-4 transition-all ${
                isSelected ? "border-apollo-orange bg-apollo-orange-light/30" : "border-border bg-white hover:border-apollo-orange/40"
              }`} data-testid={`regional-group-${g.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-xs text-text-secondary truncate">{g.ville}</div>
                    <h3 className="font-heading font-bold text-base leading-tight truncate">{g.nom}</h3>
                  </div>
                  {g.sante !== null && g.sante !== undefined ? (
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-heading font-black text-lg ${
                      g.sante >= 70 ? "bg-success/10 text-success" :
                      g.sante >= 40 ? "bg-warning/10 text-warning" :
                      "bg-danger/10 text-danger"
                    }`}>
                      {g.sante}
                    </div>
                  ) : (
                    <div className="text-xs text-text-secondary italic">N/A</div>
                  )}
                </div>
                <div className="text-xs text-text-secondary flex items-center gap-2 mb-3">
                  <Users className="w-3 h-3" /> {g.members_count} {uiLocale === "en" ? "members" : "membres"} · {t(`phase.${g.phase}`, g.phase)}
                </div>
                {g.etat_synthese && (
                  <p className="text-xs font-oracle italic text-text-primary line-clamp-2 mb-3">« {g.etat_synthese} »</p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleSelect(g.id)}
                    data-testid={`btn-select-${g.id}`}
                    className={`flex-1 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
                      isSelected ? "bg-apollo-orange text-white" : "bg-bg-soft text-text-secondary hover:bg-apollo-orange-light"
                    }`}
                  >
                    {isSelected ? (uiLocale === "en" ? "Deselect" : "Désélectionner") : (uiLocale === "en" ? "Compare" : "Comparer")}
                  </button>
                  <Link to={`/groupe/${g.id}`} data-testid={`btn-open-${g.id}`}>
                    <Button variant="ghost" size="sm" className="h-8"><ArrowRight className="w-3 h-3" /></Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {comparison.length >= 2 && (
        <section className="bg-white border border-border rounded-2xl p-6" data-testid="comparator-section">
          <h2 className="font-heading font-bold text-xl mb-4">{uiLocale === "en" ? "Comparator" : "Comparateur"}</h2>
          <div className={`grid grid-cols-1 md:grid-cols-${comparison.length} gap-4`}>
            {comparison.map((g) => (
              <div key={g.id} className="bg-bg-soft rounded-xl p-4" data-testid={`comparator-card-${g.id}`}>
                <div className="text-xs text-text-secondary">{g.ville}</div>
                <h3 className="font-heading font-bold text-base mb-3">{g.nom}</h3>
                {g.sante !== null && g.sante !== undefined && <HealthScore value={g.sante} size={110} testId={`comp-health-${g.id}`} />}
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1.5">BPM</div>
                  <BPMChart distribution={g.bpm_distribution} testId={`comp-bpm-${g.id}`} />
                </div>
                {g.latest_kpi && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1.5">{uiLocale === "en" ? "Recent KPIs" : "KPI récents"}</div>
                    <KPIDashboard latest={g.latest_kpi} testId={`comp-kpi-${g.id}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
